import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pos`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canUploadImages, setCanUploadImages] = useState(false);
  const fileInputRef = useRef(null);
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    barcode: '',
    category_id: '',
    brand_id: '',
    supplier_id: '',
    tax_group_id: '',
    mrp: '',
    base_price: '',
    selling_price: '',      // Level 1 (default/mandatory)
    selling_price_l2: '',   // Level 2 (optional)
    selling_price_l3: '',   // Level 3 (optional)
    selling_price_l4: '',   // Level 4 (optional)
    selling_price_l5: '',   // Level 5 (optional)
    current_stock: '',
    stock_alert_quantity: '',
    unit: 'pcs',
    unit_id: '',
    track_stock_separately: false,
    stock_unit: 'pcs',
    hsn_code: '',
    description: '',
    is_active: true,
    exception_for_sale: false, // Raw material - not for sale
    track_batch_expiry: false, // Enable batch & expiry tracking
    has_serial_number: false, // Enable serial number tracking
    // Additional optional settings
    size: '',
    color: '',
    age_group: '',
    expire_date: '',
    pack_size: '',
    batch_number: '',
    // Loose sales configuration
    loose_sales_type: '',
    loose_qty_options: [],
    // Primary product (shown in Quick Pick drawer)
    is_primary: false,
    print_size_in_bill: false,
    print_color_in_bill: false,
    print_description_in_bill: false
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [unitsList, setUnitsList] = useState([]);
  const [dualUnitEnabled, setDualUnitEnabled] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [looseQtyInput, setLooseQtyInput] = useState('');

  const units = ['pcs', 'kg', 'g', 'ltr', 'ml', 'box', 'pack', 'dozen', 'pair'];
  const ageGroups = ['All Ages', 'Kids', 'Teens', 'Adults', 'Seniors'];

  useEffect(() => {
    fetchAllData();
    fetchUnits();
    fetchTenantPlan();
    fetchDualUnitSetting();
  }, []);

  const fetchDualUnitSetting = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const res = await axios.get(`${API}/settings/bill`, { headers: { Authorization: `Bearer ${token}` } });
      setDualUnitEnabled(!!res.data?.enable_dual_unit_stock);
    } catch (e) {
      setDualUnitEnabled(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [search, filterCategory, filterBrand, filterLowStock]);

  const fetchAllData = async () => {
    const token = localStorage.getItem('pos_token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [catRes, brandRes, supRes, taxRes] = await Promise.all([
        axios.get(`${API}/categories`, { headers }),
        axios.get(`${API}/brands`, { headers }),
        axios.get(`${API}/suppliers`, { headers }),
        axios.get(`${API}/tax-groups`, { headers })
      ]);
      
      setCategories(catRes.data);
      setBrands(brandRes.data);
      setSuppliers(supRes.data);
      setTaxGroups(taxRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchTenantPlan = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Check if tenant has Pro or Premium plan for image uploads
      const planName = response.data.plan_name || '';
      const allowedPlans = ['pro', 'premium', 'enterprise'];
      setCanUploadImages(allowedPlans.some(p => planName.toLowerCase().includes(p)));
    } catch (error) {
      console.error('Error fetching tenant plan:', error);
      setCanUploadImages(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnitsList(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const handleUnitChange = (unitId) => {
    const unit = unitsList.find(u => u.id === unitId);
    setSelectedUnit(unit);
    setFormData({
      ...formData,
      unit_id: unitId,
      unit: unit?.short_name || 'pcs',
      // Reset loose sales config if unit doesn't allow it
      loose_sales_type: unit?.allow_loose_sales ? formData.loose_sales_type : '',
      loose_qty_options: unit?.allow_loose_sales ? formData.loose_qty_options : []
    });
  };

  const addLooseQtyOption = () => {
    const qty = parseFloat(looseQtyInput);
    if (isNaN(qty) || qty <= 0) return;
    if (formData.loose_qty_options.includes(qty)) return;
    
    setFormData({
      ...formData,
      loose_qty_options: [...formData.loose_qty_options, qty].sort((a, b) => a - b)
    });
    setLooseQtyInput('');
  };

  const removeLooseQtyOption = (qty) => {
    setFormData({
      ...formData,
      loose_qty_options: formData.loose_qty_options.filter(q => q !== qty)
    });
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const params = {};
      if (search) params.search = search;
      if (filterCategory) params.category_id = filterCategory;
      if (filterBrand) params.brand_id = filterBrand;
      if (filterLowStock) params.low_stock = true;
      
      const response = await axios.get(`${API}/products/enhanced`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Fallback to regular products endpoint
      try {
        const token = localStorage.getItem('pos_token');
        const response = await axios.get(`${API}/products`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search }
        });
        setProducts(response.data);
      } catch (err) {
        console.error('Fallback also failed:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/products/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'product_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download failed:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const token = localStorage.getItem('pos_token');
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/products/bulk-import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      await fetchAllData();
      await fetchProducts();
    } catch (error) {
      console.error('Import failed:', error);
      setImportResult({ error: error.response?.data?.detail || 'Import failed. Check the file format.' });
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return; // Prevent duplicate submission
    setSaving(true);
    
    try {
      const token = localStorage.getItem('pos_token');
      const payload = {
        ...formData,
        mrp: parseFloat(formData.mrp) || 0,
        base_price: parseFloat(formData.base_price) || 0,
        selling_price: parseFloat(formData.selling_price) || 0,
        selling_price_l2: formData.selling_price_l2 ? parseFloat(formData.selling_price_l2) : null,
        selling_price_l3: formData.selling_price_l3 ? parseFloat(formData.selling_price_l3) : null,
        selling_price_l4: formData.selling_price_l4 ? parseFloat(formData.selling_price_l4) : null,
        selling_price_l5: formData.selling_price_l5 ? parseFloat(formData.selling_price_l5) : null,
        current_stock: parseFloat(formData.current_stock) || 0,
        stock_alert_quantity: parseFloat(formData.stock_alert_quantity) || 0,
        category_id: formData.category_id || null,
        brand_id: formData.brand_id || null,
        supplier_id: formData.supplier_id || null,
        tax_group_id: formData.tax_group_id || null,
        unit_id: formData.unit_id || null,
        exception_for_sale: Boolean(formData.exception_for_sale),
        track_batch_expiry: Boolean(formData.track_batch_expiry),
        has_serial_number: Boolean(formData.has_serial_number),
        track_stock_separately: Boolean(formData.track_stock_separately),
        stock_unit: formData.track_stock_separately ? (formData.stock_unit || 'pcs') : null,
        is_primary: Boolean(formData.is_primary),
        print_size_in_bill: Boolean(formData.print_size_in_bill),
        print_color_in_bill: Boolean(formData.print_color_in_bill),
        print_description_in_bill: Boolean(formData.print_description_in_bill),
        // Additional optional settings
        size: formData.size || null,
        color: formData.color || null,
        age_group: formData.age_group || null,
        expire_date: formData.expire_date || null,
        pack_size: formData.pack_size || null,
        batch_number: formData.batch_number || null,
        // Loose sales configuration
        loose_sales_type: formData.loose_sales_type || null,
        loose_qty_options: formData.loose_qty_options?.length > 0 ? formData.loose_qty_options : null
      };

      if (editingProduct) {
        await axios.put(`${API}/products/enhanced/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/products/enhanced`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const unit = unitsList.find(u => u.id === product.unit_id || u.short_name === product.unit);
    setSelectedUnit(unit);
    // Show advanced settings if any advanced fields or loose sales config exists
    const hasAdvancedData = !!product.size || !!product.color || !!product.age_group || !!product.expire_date || !!product.pack_size || !!product.batch_number || !!product.loose_sales_type;
    setShowAdvanced(hasAdvancedData);
    setFormData({
      name: product.name,
      sku: product.sku,
      barcode: product.barcode || '',
      category_id: product.category_id || '',
      brand_id: product.brand_id || '',
      supplier_id: product.supplier_id || '',
      tax_group_id: product.tax_group_id || '',
      mrp: product.mrp?.toString() || '',
      base_price: product.base_price?.toString() || '',
      selling_price: product.selling_price?.toString() || '',
      selling_price_l2: product.selling_price_l2?.toString() || '',
      selling_price_l3: product.selling_price_l3?.toString() || '',
      selling_price_l4: product.selling_price_l4?.toString() || '',
      selling_price_l5: product.selling_price_l5?.toString() || '',
      current_stock: product.current_stock?.toString() || '',
      stock_alert_quantity: product.stock_alert_quantity?.toString() || '',
      unit: product.unit || 'pcs',
      unit_id: product.unit_id || '',
      track_stock_separately: product.track_stock_separately || false,
      stock_unit: product.stock_unit || 'pcs',
      hsn_code: product.hsn_code || '',
      description: product.description || '',
      is_active: product.is_active !== false,
      exception_for_sale: product.exception_for_sale || false,
      track_batch_expiry: product.track_batch_expiry || false,
      has_serial_number: product.has_serial_number || false,
      // Additional optional settings
      size: product.size || '',
      color: product.color || '',
      age_group: product.age_group || '',
      expire_date: product.expire_date?.split('T')[0] || '',
      pack_size: product.pack_size || '',
      batch_number: product.batch_number || '',
      // Loose sales configuration
      loose_sales_type: product.loose_sales_type || '',
      loose_qty_options: product.loose_qty_options || [],
      is_primary: product.is_primary || false,
      print_size_in_bill: product.print_size_in_bill || false,
      print_color_in_bill: product.print_color_in_bill || false,
      print_description_in_bill: product.print_description_in_bill || false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to delete'));
    }
  };

  const handleImageUpload = async (productId, file) => {
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB');
      return;
    }
    
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      alert('Only PNG and JPG images are allowed');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      await axios.post(`${API}/products/${productId}/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchProducts();
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Failed to upload image'));
    } finally {
      setUploading(false);
    }
  };

  const handleImageDelete = async (productId) => {
    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/products/${productId}/image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      barcode: '',
      category_id: '',
      brand_id: '',
      supplier_id: '',
      tax_group_id: '',
      mrp: '',
      base_price: '',
      selling_price: '',
      selling_price_l2: '',
      selling_price_l3: '',
      selling_price_l4: '',
      selling_price_l5: '',
      current_stock: '',
      stock_alert_quantity: '',
      unit: 'pcs',
      unit_id: '',
      track_stock_separately: false,
      stock_unit: 'pcs',
      hsn_code: '',
      description: '',
      is_active: true,
      exception_for_sale: false,
      track_batch_expiry: false,
      has_serial_number: false,
      size: '',
      color: '',
      age_group: '',
      expire_date: '',
      pack_size: '',
      batch_number: '',
      loose_sales_type: '',
      loose_qty_options: [],
      is_primary: false,
      print_size_in_bill: false,
      print_color_in_bill: false,
      print_description_in_bill: false
    });
    setSelectedUnit(null);
    setLooseQtyInput('');
    setShowAdvanced(false);
  };

  // Calculate profit when prices change
  const calculateProfit = () => {
    const base = parseFloat(formData.base_price) || 0;
    const selling = parseFloat(formData.selling_price) || 0;
    if (base > 0) {
      return ((selling - base) / base * 100).toFixed(1);
    }
    return '0.0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Products</h1>
          <p className="text-slate-500 text-sm">{products.length} products</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowImport(true); setImportResult(null); }}
            className="bg-white border border-violet-300 text-violet-700 px-4 py-2.5 rounded-xl font-medium hover:bg-violet-50 transition-all flex items-center gap-2"
            data-testid="import-products-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import Excel
          </button>
          <button
            onClick={() => { setShowModal(true); setEditingProduct(null); resetForm(); }}
            className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
            data-testid="add-product-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Product
          </button>
        </div>
      </div>

      {/* Bulk Import Modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="import-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">Import Products from Excel</h2>
              <button onClick={() => setShowImport(false)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="import-close-btn">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <p className="text-sm text-slate-600">
              Upload a <b>.xlsx</b> or <b>.csv</b> file. Existing products matched by <b>SKU or Barcode</b> are updated; others are created. Category, Brand and GST are created automatically if new.
            </p>

            <button
              onClick={handleDownloadTemplate}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2"
              data-testid="download-template-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Download Template
            </button>

            <input
              ref={importFileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => handleImportFile(e.target.files[0])}
              className="hidden"
              data-testid="import-file-input"
            />
            <button
              onClick={() => importFileRef.current?.click()}
              disabled={importing}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-2.5 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-60"
              data-testid="choose-import-file-btn"
            >
              {importing ? 'Importing…' : 'Choose File & Import'}
            </button>

            {importResult && (
              <div className="rounded-xl border border-slate-200 p-4 text-sm" data-testid="import-result">
                {importResult.error ? (
                  <p className="text-red-600 font-medium">{importResult.error}</p>
                ) : (
                  <div className="space-y-1">
                    <p className="text-emerald-700 font-semibold">
                      ✓ Import complete — {importResult.created} created, {importResult.updated} updated
                    </p>
                    {importResult.error_count > 0 && (
                      <div className="text-amber-700">
                        <p className="font-medium">{importResult.error_count} row(s) had errors:</p>
                        <ul className="list-disc list-inside max-h-32 overflow-y-auto">
                          {importResult.errors.map((er, i) => (
                            <li key={i}>Row {er.row}: {er.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, SKU, or barcode..."
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="search-products"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="filter-category"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <select
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="filter-brand"
          >
            <option value="">All Brands</option>
            {brands.map(brand => (
              <option key={brand.id} value={brand.id}>{brand.name}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterLowStock}
              onChange={(e) => setFilterLowStock(e.target.checked)}
              className="w-4 h-4 text-violet-600 rounded"
              data-testid="filter-low-stock"
            />
            <span className="text-sm text-slate-700">Low Stock Only</span>
          </label>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products.map((product) => (
          <div 
            key={product.id} 
            className={`bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all ${!product.is_active ? 'opacity-60' : ''}`}
            data-testid={`product-card-${product.id}`}
          >
            {/* Image */}
            <div className="relative h-40 bg-slate-100 flex items-center justify-center group">
              {product.image ? (
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
              ) : (
                <div className="text-slate-300">
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              
              {/* Image actions on hover - Only show for Pro/Premium plans */}
              {canUploadImages && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <label className="cursor-pointer px-3 py-1.5 bg-white text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-100">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(product.id, e.target.files[0])}
                    />
                    {uploading ? 'Uploading...' : 'Upload'}
                  </label>
                  {product.image && (
                    <button
                      onClick={() => handleImageDelete(product.id)}
                      className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
              
              {/* Upgrade badge for non-premium users */}
              {!canUploadImages && !product.image && (
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium">
                    Upgrade to Pro to upload images
                  </span>
                </div>
              )}
              
              {/* Stock badge */}
              {product.current_stock <= product.stock_alert_quantity && (
                <span className="absolute top-2 left-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                  Low Stock
                </span>
              )}
              
              {product.exception_for_sale && (
                <span className="absolute top-2 left-2 px-2 py-1 bg-amber-500 text-white text-xs rounded-full">
                  Raw Material
                </span>
              )}
              
              {product.track_batch_expiry && (
                <span className={`absolute ${product.exception_for_sale ? 'top-10' : 'top-2'} left-2 px-2 py-1 bg-blue-500 text-white text-xs rounded-full`}>
                  Batch Track
                </span>
              )}
              
              {product.has_serial_number && (
                <span className={`absolute ${product.track_batch_expiry ? (product.exception_for_sale ? 'top-[72px]' : 'top-10') : (product.exception_for_sale ? 'top-10' : 'top-2')} left-2 px-2 py-1 bg-orange-500 text-white text-xs rounded-full`}>
                  Serial #
                </span>
              )}
              
              {!product.is_active && (
                <span className="absolute top-2 right-2 px-2 py-1 bg-slate-500 text-white text-xs rounded-full">
                  Inactive
                </span>
              )}
            </div>

            {/* Details */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800 truncate">{product.name}</h3>
                  <p className="text-xs text-slate-500">SKU: {product.sku}</p>
                </div>
              </div>
              
              {/* Category, Brand */}
              <div className="flex flex-wrap gap-1 mb-3">
                {product.category_name && (
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full">
                    {product.category_name}
                  </span>
                )}
                {product.brand_name && (
                  <span className="px-2 py-0.5 bg-green-50 text-green-600 text-xs rounded-full">
                    {product.brand_name}
                  </span>
                )}
                {product.tax_name && (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full">
                    {product.tax_name}
                  </span>
                )}
              </div>
              
              {/* Pricing */}
              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">MRP</p>
                  <p className="font-medium text-slate-400 line-through">₹{product.mrp?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Selling</p>
                  <p className="font-bold text-violet-600">₹{product.selling_price?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Cost</p>
                  <p className="font-medium text-slate-600">₹{product.base_price?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Profit</p>
                  <p className="font-medium text-emerald-600">{product.profit_margin?.toFixed(1)}%</p>
                </div>
              </div>
              
              {/* Stock */}
              <div className="flex items-center justify-between mb-3 p-2 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Stock</span>
                <span className={`font-bold ${product.current_stock <= product.stock_alert_quantity ? 'text-red-600' : 'text-slate-800'}`}>
                  {product.current_stock} {product.track_stock_separately ? (product.stock_unit || 'pcs') : product.unit}
                </span>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(product)}
                  className="flex-1 px-3 py-2 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-colors text-sm font-medium"
                  data-testid={`edit-${product.id}`}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-slate-500">No products found. Add your first product!</p>
        </div>
      )}

      {/* Add/Edit Modal - Improved Design */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-violet-50 to-purple-50">
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <p className="text-sm text-slate-500">Fill in the product details below</p>
              </div>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-2 hover:bg-white/80 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                
                {/* Section 1: Basic Information */}
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-xs">1</span>
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-12 gap-4">
                    {/* Product Name - Full Width */}
                    <div className="col-span-12">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Product Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
                        required
                        placeholder="Enter product name"
                        data-testid="product-name-input"
                      />
                    </div>

                    {/* SKU, Barcode, HSN in one row */}
                    <div className="col-span-12 md:col-span-4">
                      <label className="block text-sm font-medium text-slate-700 mb-1">SKU</label>
                      <input
                        type="text"
                        value={formData.sku}
                        onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        placeholder="Auto-generated if empty"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        data-testid="sku-input"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-4">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Barcode</label>
                      <input
                        type="text"
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                        placeholder="Scan or enter barcode"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        data-testid="barcode-input"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-4">
                      <label className="block text-sm font-medium text-slate-700 mb-1">HSN Code</label>
                      <input
                        type="text"
                        value={formData.hsn_code}
                        onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })}
                        placeholder="For GST compliance"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        data-testid="hsn-input"
                      />
                    </div>

                    {/* Category, Brand, Supplier, Tax - 4 columns */}
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                      <select
                        value={formData.category_id}
                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        data-testid="category-select"
                      >
                        <option value="">Select</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Brand</label>
                      <select
                        value={formData.brand_id}
                        onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        data-testid="brand-select"
                      >
                        <option value="">Select</option>
                        {brands.map(brand => (
                          <option key={brand.id} value={brand.id}>{brand.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Supplier</label>
                      <select
                        value={formData.supplier_id}
                        onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        data-testid="supplier-select"
                      >
                        <option value="">Select</option>
                        {suppliers.map(sup => (
                          <option key={sup.id} value={sup.id}>{sup.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Tax Group</label>
                      <select
                        value={formData.tax_group_id}
                        onChange={(e) => setFormData({ ...formData, tax_group_id: e.target.value })}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                        data-testid="tax-group-select"
                      >
                        <option value="">No Tax</option>
                        {taxGroups.map(tax => (
                          <option key={tax.id} value={tax.id}>{tax.name} ({tax.cgst + tax.sgst}%)</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Pricing */}
                <div className="bg-emerald-50 rounded-xl p-5">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs">2</span>
                    Pricing
                  </h4>
                  <div className="grid grid-cols-12 gap-4">
                    {/* Main Prices */}
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Cost Price <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.base_price}
                          onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                          className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                          required
                          data-testid="base-price-input"
                        />
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">MRP <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.mrp}
                          onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                          className="w-full pl-8 pr-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                          required
                          data-testid="mrp-input"
                        />
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Selling (L1) <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.selling_price}
                          onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                          className="w-full pl-8 pr-3 py-2.5 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-emerald-50 font-medium"
                          required
                          data-testid="selling-price-input"
                        />
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Profit Margin</label>
                      <div className="px-4 py-2.5 bg-white border border-emerald-200 rounded-lg text-emerald-700 font-bold text-center">
                        {calculateProfit()}%
                      </div>
                    </div>

                    {/* Multi-Level Pricing */}
                    <div className="col-span-12">
                      <div className="bg-white/60 rounded-lg p-4 mt-2">
                        <p className="text-xs font-medium text-slate-600 mb-3">Customer Level Pricing (Optional)</p>
                        <div className="grid grid-cols-4 gap-3">
                          {[2, 3, 4, 5].map(level => {
                            const fieldName = `selling_price_l${level}`;
                            const price = parseFloat(formData[fieldName]) || 0;
                            const basePrice = parseFloat(formData.base_price) || 0;
                            const margin = basePrice > 0 && price > 0 ? (((price - basePrice) / basePrice) * 100).toFixed(1) : null;
                            return (
                              <div key={level}>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Level {level}</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData[fieldName]}
                                  onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
                                  placeholder="₹"
                                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                                />
                                {margin && <p className="text-xs text-emerald-600 mt-1">{margin}%</p>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Inventory & Unit */}
                <div className="bg-blue-50 rounded-xl p-5">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">3</span>
                    Inventory & Stock
                  </h4>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Current Stock{dualUnitEnabled && formData.track_stock_separately ? ` (in ${formData.stock_unit || 'pcs'})` : ''}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.current_stock}
                        onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="stock-input"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Low Stock Alert</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.stock_alert_quantity}
                        onChange={(e) => setFormData({ ...formData, stock_alert_quantity: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="stock-alert-input"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-6">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Unit of Measure</label>
                      <select
                        value={formData.unit_id || formData.unit}
                        onChange={(e) => {
                          const isUnitId = unitsList.find(u => u.id === e.target.value);
                          if (isUnitId) {
                            handleUnitChange(e.target.value);
                          } else {
                            setFormData({ ...formData, unit: e.target.value, unit_id: '' });
                            setSelectedUnit(null);
                          }
                        }}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="unit-select"
                      >
                        {unitsList.length > 0 ? (
                          <>
                            <option value="">Select Unit</option>
                            {unitsList.filter(u => u.is_active).map(u => (
                              <option key={u.id} value={u.id}>{u.name} ({u.short_name})</option>
                            ))}
                          </>
                        ) : (
                          units.map(u => (
                            <option key={u} value={u}>{u}</option>
                          ))
                        )}
                      </select>
                      {selectedUnit?.allow_loose_sales && (
                        <p className="mt-1 text-xs text-blue-600">✓ Loose sales enabled for this unit</p>
                      )}
                    </div>

                    {/* Dual-unit stock (only when tenant setting is enabled) */}
                    {dualUnitEnabled && (
                      <div className="col-span-12">
                        <label className="flex items-center gap-2 cursor-pointer bg-amber-50 px-4 py-2.5 rounded-lg border border-amber-200">
                          <input
                            type="checkbox"
                            checked={formData.track_stock_separately}
                            onChange={(e) => setFormData({ ...formData, track_stock_separately: e.target.checked })}
                            className="w-4 h-4 text-amber-600 rounded"
                            data-testid="track-stock-separately-checkbox"
                          />
                          <span className="text-sm text-slate-700">
                            Track stock in a different unit (sell in <b>{formData.unit || 'unit'}</b>, stock in pcs)
                          </span>
                        </label>
                        {formData.track_stock_separately && (
                          <div className="mt-2 md:w-1/2">
                            <label className="block text-sm font-medium text-slate-700 mb-1">Stock Unit (purchase/stock count)</label>
                            <select
                              value={formData.stock_unit}
                              onChange={(e) => setFormData({ ...formData, stock_unit: e.target.value })}
                              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                              data-testid="stock-unit-select"
                            >
                              {units.map(u => (<option key={u} value={u}>{u}</option>))}
                            </select>
                            <p className="mt-1 text-xs text-amber-700">Purchase, stock & reports stay in this unit. Cashier enters pieces at billing; the price uses the sale unit.</p>
                          </div>
                        )}
                      </div>
                    )}


                    {/* Product Flags */}
                    <div className="col-span-12">
                      <div className="flex flex-wrap gap-4 pt-2">
                        <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-violet-300 transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="w-4 h-4 text-violet-600 rounded"
                            data-testid="is-active-checkbox"
                          />
                          <span className="text-sm text-slate-700">Active Product</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-amber-300 transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.exception_for_sale}
                            onChange={(e) => setFormData({ ...formData, exception_for_sale: e.target.checked })}
                            className="w-4 h-4 text-amber-600 rounded"
                            data-testid="exception-for-sale-checkbox"
                          />
                          <span className="text-sm text-slate-700">Raw Material</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.track_batch_expiry}
                            onChange={(e) => setFormData({ ...formData, track_batch_expiry: e.target.checked })}
                            className="w-4 h-4 text-blue-600 rounded"
                            data-testid="track-batch-expiry-checkbox"
                          />
                          <span className="text-sm text-slate-700">Track Batch & Expiry</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-orange-300 transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.has_serial_number}
                            onChange={(e) => setFormData({ ...formData, has_serial_number: e.target.checked })}
                            className="w-4 h-4 text-orange-600 rounded"
                            data-testid="has-serial-number-checkbox"
                          />
                          <span className="text-sm text-slate-700">Has Serial Numbers</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border border-slate-200 hover:border-emerald-300 transition-colors" title="Show this product in the Quick Pick drawer on POS Billing">
                          <input
                            type="checkbox"
                            checked={formData.is_primary}
                            onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                            className="w-4 h-4 text-emerald-600 rounded"
                            data-testid="is-primary-checkbox"
                          />
                          <span className="text-sm text-slate-700">Mark as Primary (Quick Pick)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loose Sales Configuration */}
                {selectedUnit?.allow_loose_sales && (
                  <div className="bg-violet-50 rounded-xl p-5">
                    <h4 className="text-sm font-bold text-violet-700 uppercase tracking-wide mb-4">Loose Sales Configuration</h4>
                    <div className="space-y-4">
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border-2 border-slate-200 hover:border-violet-300 transition-colors">
                          <input
                            type="radio"
                            name="loose_type"
                            value="fixed"
                            checked={formData.loose_sales_type === 'fixed'}
                            onChange={(e) => setFormData({ ...formData, loose_sales_type: e.target.value })}
                            className="w-4 h-4 text-violet-600"
                          />
                          <span className="text-sm">Fixed Options</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border-2 border-slate-200 hover:border-violet-300 transition-colors">
                          <input
                            type="radio"
                            name="loose_type"
                            value="editable"
                            checked={formData.loose_sales_type === 'editable'}
                            onChange={(e) => setFormData({ ...formData, loose_sales_type: e.target.value })}
                            className="w-4 h-4 text-violet-600"
                          />
                          <span className="text-sm">Free Input</span>
                        </label>
                      </div>

                      {formData.loose_sales_type === 'fixed' && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Quantity Options ({selectedUnit?.short_name})</label>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {formData.loose_qty_options.map(qty => (
                              <span key={qty} className="px-3 py-1 bg-white border border-violet-200 rounded-full text-sm flex items-center gap-2">
                                {qty} {selectedUnit?.short_name}
                                <button type="button" onClick={() => removeLooseQtyOption(qty)} className="text-red-500 hover:text-red-700">×</button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              step="0.001"
                              value={looseQtyInput}
                              onChange={(e) => setLooseQtyInput(e.target.value)}
                              placeholder="e.g., 0.25"
                              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                              data-testid="loose-qty-input"
                            />
                            <button
                              type="button"
                              onClick={addLooseQtyOption}
                              className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700"
                              data-testid="add-loose-qty-btn"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Section 4: Additional Details (Collapsible) */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-sm font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                      <span className="w-6 h-6 bg-slate-400 text-white rounded-full flex items-center justify-center text-xs">4</span>
                      Additional Details
                    </span>
                    <svg className={`w-5 h-5 text-slate-400 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showAdvanced && (
                    <div className="px-5 pb-5 border-t border-slate-100">
                      <div className="grid grid-cols-12 gap-4 pt-4">
                        <div className="col-span-6 md:col-span-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Size</label>
                          <input
                            type="text"
                            value={formData.size}
                            onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                            placeholder="S, M, L, XL..."
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                            data-testid="size-input"
                          />
                          <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer text-xs text-slate-600">
                            <input type="checkbox" checked={formData.print_size_in_bill}
                              onChange={(e) => setFormData({ ...formData, print_size_in_bill: e.target.checked })}
                              className="w-3.5 h-3.5 text-emerald-600 rounded"
                              data-testid="print-size-in-bill" />
                            Print in Bill
                          </label>
                        </div>
                        <div className="col-span-6 md:col-span-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
                          <input
                            type="text"
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            placeholder="Red, Blue..."
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                            data-testid="color-input"
                          />
                          <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer text-xs text-slate-600">
                            <input type="checkbox" checked={formData.print_color_in_bill}
                              onChange={(e) => setFormData({ ...formData, print_color_in_bill: e.target.checked })}
                              className="w-3.5 h-3.5 text-emerald-600 rounded"
                              data-testid="print-color-in-bill" />
                            Print in Bill
                          </label>
                        </div>
                        <div className="col-span-6 md:col-span-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Age Group</label>
                          <select
                            value={formData.age_group}
                            onChange={(e) => setFormData({ ...formData, age_group: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                            data-testid="age-group-select"
                          >
                            <option value="">Select</option>
                            {ageGroups.map(ag => (
                              <option key={ag} value={ag}>{ag}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-6 md:col-span-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Expiry Date</label>
                          <input
                            type="date"
                            value={formData.expire_date}
                            onChange={(e) => setFormData({ ...formData, expire_date: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                            data-testid="expire-date-input"
                          />
                        </div>
                        <div className="col-span-6 md:col-span-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Pack Size</label>
                          <input
                            type="text"
                            value={formData.pack_size}
                            onChange={(e) => setFormData({ ...formData, pack_size: e.target.value })}
                            placeholder="Pack of 6, 500g..."
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                            data-testid="pack-size-input"
                          />
                        </div>
                        <div className="col-span-6 md:col-span-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Batch Number</label>
                          <input
                            type="text"
                            value={formData.batch_number}
                            onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                            placeholder="Optional"
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                            data-testid="batch-number-input"
                          />
                        </div>
                        <div className="col-span-12">
                          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows="2"
                            placeholder="Product description..."
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                            data-testid="description-input"
                          />
                          <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer text-xs text-slate-600">
                            <input type="checkbox" checked={formData.print_description_in_bill}
                              onChange={(e) => setFormData({ ...formData, print_description_in_bill: e.target.checked })}
                              className="w-3.5 h-3.5 text-emerald-600 rounded"
                              data-testid="print-description-in-bill" />
                            Print in Bill
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>

              {/* Footer */}
              <div className="p-5 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3 sticky bottom-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors font-medium"
                  data-testid="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-8 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-50"
                  data-testid="save-product-btn"
                >
                  {saving ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
